/* eslint-disable react/no-array-index-key */
import React, { useContext, useMemo, useEffect } from 'react'
import {
  APPLICATION_STATE,
  ApplicationContext
} from '@apps/contexts/ApplicationContext'
import { PLANS_STATE } from '@apps/redux/features/PlansSlice'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import DealerActivationPaths from '@sites/dealer/activation/DealerActivationPaths'
import ActivationSteps from '@sites/shared/Activation/ActivationSteps'
import { useHistory, useLocation } from 'react-router-dom'
import { getBySlug, getById } from '@apps/utils/contentful'
import Sites from '@sites/index'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { setActiveStepNumber } from '@apps/redux/features/CommonSlice'
import { StepTracker as KoodoStepTracker } from '@telus/koodo-ui-kit'

const StepTracker = ({ activeStepIndex, noWrapper = false }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const consumerActivationPaths = new ConsumerActivationPaths()
  const dealerActivationPaths = new DealerActivationPaths()

  const { userType, content: { pageContent } = {} } = useSelector((state) => state.common)
  const appState = useContext(ApplicationContext)
  const {
    [APPLICATION_STATE.CURRENT_REGION]: currentRegion,
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language
  } = appState

  const { [PLANS_STATE.PRE_SELECTED_PLAN]: preSelectedPlan } = useSelector(
    (state) => state.plansPage
  )

  const commonElementsPage = getById(pageContent, 'commonElements')
  const shortTextFields = commonElementsPage?.[language]?.shortTextFields || []

  useEffect(() => {
    dispatch(setActiveStepNumber(activeStepIndex))
  }, [activeStepIndex])

  const textContent = useMemo(() => {
    if (!shortTextFields) return {}
    return {
      accountSetup: getBySlug(shortTextFields, 'step-tracker-account-setup')?.value,
      phoneNumber: getBySlug(shortTextFields, 'step-tracker-phone-number')?.value,
      plans: getBySlug(shortTextFields, 'step-tracker-plans')?.value,
      payment: getBySlug(shortTextFields, 'step-tracker-payment')?.value,
      selectSIM: getBySlug(shortTextFields, 'step-tracker-select-sim')?.value
    }
  }, [shortTextFields])

  const steps = useMemo(() => {
    const routes = userType === Sites.dealer ? dealerActivationPaths : consumerActivationPaths
    const usePreSelectedPlanSteps = userType === Sites.consumer && preSelectedPlan
    const activationSteps = new ActivationSteps(routes, usePreSelectedPlanSteps, {
      accountSetup: textContent.accountSetup,
      simSelection: textContent.selectSIM,
      chooseNumber: textContent.phoneNumber,
      plans: textContent.plans,
      payment: textContent.payment
    })
    return activationSteps.NavigationSteps
  }, [userType, textContent, preSelectedPlan, currentRegion])

  const koodoSteps = useMemo(
    () => steps.map((step) => ({ label: step.label, href: step.path })),
    [steps]
  )

  const handleStepClick = (stepNumber, event) => {
    const target = steps[stepNumber - 1]
    if (!target) return
    event.preventDefault()
    history.push(target.path, { from: location.pathname })
  }

  // `noWrapper`: skip the legacy padded container when rendered inside a
  // parent that already provides max-width + horizontal padding.
  const tracker = (
    <KoodoStepTracker
      steps={koodoSteps}
      activeStepIndex={activeStepIndex}
      onStepClick={handleStepClick}
    />
  )

  // `noWrapper`: skip the legacy padded container when rendered inside a
  // parent that already provides max-width + horizontal padding (e.g.
  // CheckoutLayout). The `data-testid="step-tracker"` hook is preserved
  // either way so existing selectors / QA automation keep working.
  if (noWrapper) {
    return <div data-testid="step-tracker">{tracker}</div>
  }

  return (
    <div data-testid="step-tracker" className="max-w-6xl mx-4 md:m-auto md:px-4 md:pb-12 pt-5 mb-5">
      {tracker}
    </div>
  )
}

StepTracker.propTypes = {
  activeStepIndex: PropTypes.number.isRequired,
  noWrapper: PropTypes.bool
}

StepTracker.defaultProps = {
  noWrapper: false
}

export default StepTracker
